import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { Order, OrderProduct } from '../../api-client';
import { IUpdateDeliveryDate } from '../../common/models/Slices';
import { ORDER_STATUS } from '../../utils/constants/order-status';
import { STORAGE_KEYS } from '../../utils/constants/storage-keys';
import {
	manipulateSizeQuantity,
	productEqualityComparer,
} from '../../utils/data-format-enchanters/order-products-enchanter';
import { sessionStorageHelper } from '../../utils/storage-helpers/session-storage-helper';

// Define a type for the slice state
interface OrderSummaryState extends Order {}

// Define the initial state using that type
const initialState: OrderSummaryState = {
	status: ORDER_STATUS.DRAFT,
	products: [],
};

export const orderSummarySlice = createSlice({
	name: 'orderSummary',
	// `createSlice` will infer the state type from the `initialState` argument
	initialState,
	reducers: {
		upsertOrder: (state, action: PayloadAction<Order>) => {
			Object.assign(state, action.payload);

			sessionStorageHelper.setStorageItem(
				STORAGE_KEYS.BV_ORDER_SUMMARY,
				action.payload,
			);
		},
		destroyOrderProducts: (state) => {
			state.products = [];

			sessionStorageHelper.setStorageItem(STORAGE_KEYS.BV_ORDER_SUMMARY, state);
		},
		keepProduct: (state, action: PayloadAction<OrderProduct>) => {
			const payload = action.payload;

			const products = state.products ?? []; // <-- sempre array

			const hasSameRowId = products.some((p) => p.rowId === payload.rowId);

			if (hasSameRowId) {
				state.products = products.map((p) =>
					p.rowId === payload.rowId ? payload : p,
				);
				sessionStorageHelper.setStorageItem(
					STORAGE_KEYS.BV_ORDER_SUMMARY,
					state,
				);
				return;
			}

			const hasDuplicateSameProduct = products.some(
				(p) => p.rowId !== payload.rowId && productEqualityComparer(p, payload),
			);

			if (hasDuplicateSameProduct) {
				state.products = products.map((p) => {
					if (!productEqualityComparer(p, payload)) return p;

					return {
						...p,
						sizes: p.sizes?.map((size) => ({
							...size,
							quantity: manipulateSizeQuantity(size, payload, true),
						})),
					};
				});
			} else {
				state.products = [...products, payload];
			}

			sessionStorageHelper.setStorageItem(STORAGE_KEYS.BV_ORDER_SUMMARY, state);
		},

		updateProductsDeliveryDate: (
			state,
			action: PayloadAction<IUpdateDeliveryDate>,
		) => {
			if (action.payload.ids) {
				state.products = _.map(state.products, (stateProduct) =>
					_.includes(action.payload.ids, stateProduct.rowId)
						? { ...stateProduct, deliveryDate: action.payload.deliveryDate }
						: stateProduct,
				);

				sessionStorageHelper.setStorageItem(
					STORAGE_KEYS.BV_ORDER_SUMMARY,
					state,
				);
			}
		},
		deleteProduct: (state, action: PayloadAction<number>) => {
			state.products = _.filter(
				state.products,
				(entry) => entry.rowId !== action.payload,
			);

			sessionStorageHelper.setStorageItem(STORAGE_KEYS.BV_ORDER_SUMMARY, state);
		},
	},
});

export const {
	upsertOrder,
	destroyOrderProducts,
	keepProduct,
	updateProductsDeliveryDate,
	deleteProduct,
} = orderSummarySlice.actions;

export default orderSummarySlice.reducer;
